import { Scope } from '@developerz/domain';
import { IconAlertCircle } from '@developerz/ui';
import { type JSX, Show } from 'solid-js';
import { AgentHoursPanel } from '../components/AgentHoursPanel';
import { BillingPlanCard } from '../components/BillingPlanCard';
import { BillingUsage } from '../components/BillingUsage';
import { CardGridSkeleton } from '../components/CardGridSkeleton';
import { EmptyState } from '../components/EmptyState';
import { ErrorState } from '../components/ErrorState';
import { OssVerifyPanel } from '../components/OssVerifyPanel';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { SeatCheckout } from '../components/SeatCheckout';
import { StatusPill } from '../components/StatusPill';
import { Toast } from '../components/Toast';
import { Button } from '../components/ui/Button';
import { t } from '../i18n';
import { isForbidden } from '../lib/api';
import {
  openBillingPortal,
  portalErrorCopy,
  usageErrorCopy,
  useSeatOffer,
  useUsage,
} from '../lib/billing';
import { useScopeVerdict } from '../lib/scope-gate';
import { accountKind, SESSION_TOKEN, useMe } from '../lib/session';
import { createToast } from '../lib/toast';

// Usage + billing view (#157 data, control-plane redesign). Container — fetches
// the usage rollup and frames it per the mock Billing(): paused banner, then an
// xl:2 grid of the Plan panel (entitlements, portal action) and the Usage &
// spend panel (spend vs cap, handled units, cap editor). `token` is
// injectable for tests; the router mounts this with no props → dev session.

export function Billing(props: { token?: string | null } = {}): JSX.Element {
  const token = props.token ?? SESSION_TOKEN;
  const query = useUsage(token);
  const { toast, notify, dismiss } = createToast();
  // No token = prod pre-SSO: `useUsage` stays disabled, so distinguish "no
  // session" from "loading" rather than spin forever.
  const hasSession = token !== null;
  // Offer a first subscription to a billing-capable caller with no seat plan.
  const seatOffer = useSeatOffer(token);
  // The cart needs the account's KIND: Solo is personal-accounts-only, so an
  // org must never be offered it (`SeatCheckout`). `/v1/me` is the one place the
  // dashboard learns that, and `useSeatOffer` already reads it — same query key,
  // so this is a cache read, not a second request.
  const me = useMe(token);
  // This page's billing WRITES — the two portal buttons, the cap PATCH through
  // BillingUsage — are `write:billing` actions, so the settled verdict gates
  // the executable controls: 'denied' and 'unsettled' hide them while the
  // banner text and spend facts stay for everyone, and no you-can't line is
  // added that 'unsettled' would have to flip (scope-gate's three-valued rule).
  const mayManageBilling = useScopeVerdict(Scope.WriteBilling, token);

  // Stripe Portal is the manage/fix path (paused banner + Plan action). Free
  // accounts get a 409 (nothing to manage). Failure goes through the page toast
  // like every other mutation here (#924) — `window.alert` was off-design and
  // blocked the page on a surface that already owns a `<Toast/>`.
  const portal = (): void => {
    void openBillingPortal(token).catch((e: unknown) => notify(portalErrorCopy(e), 'error'));
  };

  return (
    <>
      <PageHeader
        title={t('billing.title')}
        desc={t('billing.subtitle')}
        actions={
          <Show when={query.data}>
            {(u) => <StatusPill>{t('billing.window', { window: u().window })}</StatusPill>}
          </Show>
        }
      />

      {/* Buy seats (arc 101 slice 07). Reads entitlements, not the usage rollup,
          so it stands outside the block below: an account with no plan is exactly
          the one whose usage view is least interesting, and a usage hiccup must
          not take the only self-serve path to a first subscription down with it. */}
      <Show when={seatOffer()}>
        <Panel title={t('billing.buy_seats_heading')}>
          <p class="billing-buy__desc">{t('billing.buy_seats_desc')}</p>
          <SeatCheckout token={token} accountKind={accountKind(me.data?.kind)} onNotice={notify} />
        </Panel>
      </Show>

      {/* Apply for OSS Verified (#68). Outside the usage block for the same
          reason as the seat cart: it reads its own endpoint, and an account
          asking to stop being billed is precisely the one whose usage rollup
          must not be able to hide the ask. */}
      <OssVerifyPanel token={token} onNotice={notify} />

      <Show
        when={hasSession ? query.data : undefined}
        fallback={
          !hasSession ? (
            <EmptyState message={t('billing.no_session')} />
          ) : query.isError ? (
            /* A 403 gets NO Retry — it is an answer, refused identically on
               every re-ask. `undefined` IS the way to say that: `ErrorState`
               renders the button on the prop's truthiness. */
            <ErrorState
              message={usageErrorCopy(query.error)}
              error={query.error}
              onRetry={isForbidden(query.error) ? undefined : () => void query.refetch()}
            />
          ) : (
            <div role="status">
              <span class="sr-only">{t('billing.loading')}</span>
              <CardGridSkeleton listClass="billing-grid" count={2} />
            </div>
          )
        }
      >
        {(u) => (
          <>
            {/* Paused banner (#235): billing_paused ⇒ a Stripe payment failed and
                dispatch is stopped. Surface it + the portal fix path. */}
            <Show when={u().billing_paused}>
              <div class="alert alert--icon alert--destructive" role="alert">
                <IconAlertCircle />
                <p class="alert__title">{t('billing.paused_title')}</p>
                <p class="alert__desc">{t('billing.paused_note')}</p>
                <div class="alert__action">
                  <Show when={mayManageBilling() === 'allowed'}>
                    <Button size="sm" onClick={portal}>
                      {t('billing.manage_billing')}
                    </Button>
                  </Show>
                </div>
              </div>
            </Show>

            <div class="billing-grid">
              <Panel
                title={t('billing.plan')}
                action={
                  <Show when={mayManageBilling() === 'allowed'}>
                    <Button variant="outline" size="sm" onClick={portal}>
                      {t('billing.manage_subscription')}
                    </Button>
                  </Show>
                }
              >
                <BillingPlanCard token={token} />
              </Panel>

              <Panel title={t('billing.usage_heading')}>
                <BillingUsage
                  usage={u()}
                  token={token}
                  maySetCap={() => mayManageBilling() === 'allowed'}
                  onCapSaved={() => void query.refetch()}
                  onNotice={notify}
                />
              </Panel>
              {/* Agent-hours ceiling (arc 101 slice 01) — its own panel: Usage &
                  spend meters dollars, this meters the free tier's compute. It
                  renders away when the server sends no band. */}
              <AgentHoursPanel hours={u().agent_hours} />
            </div>
          </>
        )}
      </Show>
      <Toast
        message={toast()?.message ?? null}
        tone={toast()?.tone ?? 'success'}
        onDismiss={dismiss}
      />
    </>
  );
}
